import React from 'react';

import { StudyRulesContent } from './styled';

// The supplied rules use an example learning path, not the student's account data.
export default function StudyRules() {
	const opened = new Date(2026, 7, 30).getTime();
	const deadline = new Date(2027, 5, 30).getTime();
	const now = Date.now();
	const daysLeft = Math.max(0, Math.ceil((deadline - now) / 86400000));
	const timeUsed = Math.round(
		Math.min(100, Math.max(0, ((now - opened) / (deadline - opened)) * 100)),
	);

	return (
		<StudyRulesContent>
			<header style={{ display: 'grid', gap: '6px', padding: '4px 4px 0' }}>
				<div className="eyebrow">Study Rules</div>
				<h1>OSSD Dual Diploma Program</h1>
				<p className="muted">
					Program length, course deadlines, graduation requirements, completion rules and
					assessment.
				</p>
			</header>

			<section className="two">
				<div className="card">
					<div>
						<div className="eyebrow">Your program · Example</div>
						<h2 className="big10">Expected graduation date</h2>
						<p className="sub">
							Your learning path starts in the grade you joined and finishes at the end of Grade 12.
							Your courses are scheduled across these years.
						</p>
					</div>
					<div className="grad-date">
						<span>3-year program</span>
						<b>June 2029</b>
						<small>
							3-year program starting in the 2026–2027 school year. Graduation is always in June.
						</small>
					</div>
					<div className="years" aria-label="Program years">
						<div className="yr now">
							<b>Grade 10</b>
							<span>2026–2027</span>
							<em>You are here</em>
						</div>
						<div className="yr">
							<b>Grade 11</b>
							<span>2027–2028</span>
						</div>
						<div className="yr">
							<b>Grade 12</b>
							<span>2028–2029</span>
						</div>
						<div className="yr grad">
							<b>OSSD</b>
							<span>June 2029</span>
						</div>
					</div>
					<div className="table-scroll">
						<table className="entry">
							<caption>Grade of entry in 2026–2027</caption>
							<thead>
								<tr>
									<th aria-label="Entry grade" />
									<th>Grade 9</th>
									<th>Grade 10</th>
									<th>Grade 11</th>
									<th>Grade 12</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<th>Program</th>
									<td>4 years</td>
									<td className="on">3 years</td>
									<td>2 years</td>
									<td>1 year</td>
								</tr>
								<tr>
									<th>Graduation</th>
									<td>June 2030</td>
									<td className="on">June 2029</td>
									<td>June 2028</td>
									<td>June 2027</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
				<div className="card">
					<div>
						<div className="eyebrow">Course deadline · Example</div>
						<h2 className="big10">Up to 10 months per course</h2>
						<p className="sub">
							10 months from the date a course opens is the maximum time to complete it, including
							the final exam. The deadline on a course card applies to that course only, not to the
							whole program.
						</p>
					</div>
					<ul className="pace">
						<li>
							<b>You can finish earlier.</b> A course can be completed in less than 10 months.
						</li>
						<li>
							<b>You can start the next course</b> before the current one is finished, once it is
							open for you.
						</li>
						<li>
							<b>Plan your time</b> so that all courses of your path are completed by your expected
							graduation date.
						</li>
					</ul>

					<div className="course">
						<div className="top">
							<div>
								<h3>International Business · BBB4M</h3>
								<span className="muted" style={{ fontSize: '13px' }}>
									Opened 30.08.2026
								</span>
							</div>
							<span className={`pill ${daysLeft <= 60 ? 'p-warn' : 'p-ok'}`}>
								{daysLeft > 0 ? `${daysLeft} days left` : 'Deadline passed'}
							</span>
						</div>
						<div
							className="bar"
							role="progressbar"
							aria-label="Example course time used"
							aria-valuemin={0}
							aria-valuemax={100}
							aria-valuenow={timeUsed}
						>
							<i style={{ width: `${timeUsed}%` }} />
						</div>
						<div className="meta">
							<span>
								Time used <b>{timeUsed}% of 10 months</b>
							</span>
							<span>
								Course deadline <b>30.06.2027</b>
							</span>
						</div>
					</div>
				</div>
			</section>

			<section className="alert">
				<h2>Missed course deadline</h2>
				<ul>
					<li>
						The course is recorded as <b>failed</b>.
					</li>
					<li>
						The course must be <b>retaken in full</b>, and the retake requires a <b>new payment</b>.
					</li>
					<li>Courses that require it as a prerequisite remain locked until it is passed.</li>
					<li>Students who fall behind should contact their mentor as early as possible.</li>
				</ul>
			</section>

			<section className="card">
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						gap: '10px',
						flexWrap: 'wrap',
						alignItems: 'end',
					}}
				>
					<div>
						<h2>My learning path</h2>
						<p className="sub">Computer Science · 3-year program · 7 credits</p>
					</div>
					<span className="example">Example</span>
				</div>
				<div className="stats">
					<div className="stat s-done">
						<div className="n">0</div>
						<span>Completed</span>
					</div>
					<div className="stat s-active">
						<div className="n">1</div>
						<span>Active now</span>
					</div>
					<div className="stat s-paid">
						<div className="n">0</div>
						<span>Paid, opens later</span>
					</div>
					<div className="stat s-todo">
						<div className="n">6</div>
						<span>Not purchased yet</span>
					</div>
				</div>
				<div className="list">
					<div className="row">
						<b>International Business · BBB4M</b>
						<span className="badge b-active">Active</span>
						<small>Grade 10 · course deadline 30.06.2027</small>
					</div>
					<div className="row">
						<b>World Cultures · HSC4M</b>
						<span className="badge b-buy">Available to buy</span>
						<small>Grade 10 · opens after payment</small>
					</div>
					<div className="row">
						<b>Canadian and World Issues · CGW4U</b>
						<span className="badge b-lock">Opens in Grade 11</span>
						<small>Can be purchased now; opens in Grade 11</small>
					</div>
					<div className="row">
						<b>Advanced Functions · MHF4U</b>
						<span className="badge b-lock">Opens in Grade 11</span>
						<small>Needs Grade 11 math (MCR3U) first</small>
					</div>
					<div className="row">
						<b>Literacy Course · OLC4O (OSSLC)</b>
						<span className="badge b-lock">Opens in Grade 11</span>
						<small>Graduation requirement</small>
					</div>
					<div className="row">
						<b>English 12 · ENG4U</b>
						<span className="badge b-lock">Opens in Grade 12</span>
						<small>Needs Grade 11 English</small>
					</div>
					<div className="row">
						<b>Calculus · MCV4U</b>
						<span className="badge b-lock">Opens in Grade 12</span>
						<small>Needs Advanced Functions (MHF4U) first</small>
					</div>
				</div>
			</section>

			<section className="card">
				<div>
					<h2>Graduation requirements</h2>
					<p className="sub">To receive the OSSD, a student must complete all of the following.</p>
				</div>
				<div className="reqs">
					<div className="req">
						<div className="v">6+</div>
						<h3>Ontario credits with TIS</h3>
						<p>
							All credits of the student’s learning path, each completed with a final mark of 50% or
							higher.
						</p>
					</div>
					<div className="req">
						<div className="v">OSSLC</div>
						<h3>literacy course</h3>
						<p>
							Successful completion of the Ontario Secondary School Literacy Course (OLC4O).
							Included in every learning path.
						</p>
					</div>
					<div className="req">
						<div className="v">20</div>
						<h3>community involvement hours</h3>
						<p>
							Volunteer activities confirmed by a letter from the organization and uploaded to the
							student account.
						</p>
					</div>
				</div>
			</section>

			<section className="card">
				<h2>Course completion rules</h2>
				<ol className="steps">
					<li>
						<h3>Purchase</h3>
						<p>Credits can be purchased individually or as a full learning path in Billing.</p>
					</li>
					<li>
						<h3>Access</h3>
						<p>
							A course opens when the student reaches its grade and has passed its prerequisite.
						</p>
					</li>
					<li>
						<h3>Assignments</h3>
						<p>All assignments must be submitted. Each one counts toward the final mark.</p>
					</li>
					<li>
						<h3>Progress</h3>
						<p>Students are expected to monitor their marks in Gradebook regularly.</p>
					</li>
					<li>
						<h3>Final exam</h3>
						<p>The final exam must be completed before the course deadline.</p>
					</li>
				</ol>
				<div className="pass">
					<div className="big">50%</div>
					<p>
						<b>Minimum final mark required to earn a credit.</b> A course with a final mark below
						50% is not passed, and the credit is not awarded.
					</p>
				</div>
			</section>

			<section className="card">
				<div>
					<h2>Assessment</h2>
					<p className="sub">
						Each course uses three types of assessment. They have different purposes, and only one
						of them forms your overall grade.
					</p>
				</div>
				<div className="assess">
					<article className="as t-as">
						<div className="as-top">
							<span className="as-kind">Assessment as learning</span>
							<span className="as-badge">Graded</span>
						</div>
						<h3>Check your own understanding</h3>
						<p>
							Self-checks, reflections and practice tasks. They help you see what you already know
							and what to review.
						</p>
						<dl>
							<dt>Result</dt>
							<dd>
								Status <b>Graded</b> after submission. No mark.
							</dd>
							<dt>Overall grade</dt>
							<dd>Not included</dd>
						</dl>
					</article>
					<article className="as t-for">
						<div className="as-top">
							<span className="as-kind">Assessment for learning</span>
							<span className="as-badge">Mark + feedback</span>
						</div>
						<h3>Get feedback from your teacher</h3>
						<p>
							Assignments during the course. Your teacher gives a mark and comments so you can
							improve before the assessments of learning.
						</p>
						<dl>
							<dt>Result</dt>
							<dd>
								A <b>mark</b> and teacher feedback.
							</dd>
							<dt>Overall grade</dt>
							<dd>Not included</dd>
						</dl>
					</article>
					<article className="as t-of">
						<div className="as-top">
							<span className="as-kind">Assessment of learning</span>
							<span className="as-badge">Most important</span>
						</div>
						<h3>Show what you have learned</h3>
						<p>
							Unit tests, major assignments and the final exam. They measure what you have achieved
							in the course.
						</p>
						<dl>
							<dt>Result</dt>
							<dd>
								A <b>mark</b> that is recorded.
							</dd>
							<dt>Overall grade</dt>
							<dd>
								<b>Included</b>
							</dd>
						</dl>
					</article>
				</div>
				<div className="flow" aria-label="How the overall grade is formed">
					<span className="f-step">As learning</span>
					<span className="f-arr">practice</span>
					<span className="f-step">For learning</span>
					<span className="f-arr">improve</span>
					<span className="f-step f-of">Of learning</span>
					<span className="f-arr">counts</span>
					<span className="f-step f-res">Overall grade · pass is 50%</span>
				</div>
				<div className="tips">
					<h3>What to pay attention to</h3>
					<ul>
						<li>
							Submit <b>every</b> assessment, including the ones without a mark. They prepare you
							for the assessments that count.
						</li>
						<li>
							Read your teacher’s feedback on assessments for learning and use it in the next task.
						</li>
						<li>
							Assessments of learning and the final exam decide your overall grade. Plan time for
							them before the course deadline.
						</li>
						<li>Check the status and marks of each assessment in Gradebook every week.</li>
					</ul>
				</div>
			</section>

			<section className="card">
				<h2>Frequently asked questions</h2>
				<div>
					<details open>
						<summary>Is the deadline on a course the deadline for the whole program?</summary>
						<p>
							No. It applies to that course only. The program continues until the end of Grade 12,
							and courses are scheduled across the grades of the learning path.
						</p>
					</details>
					<details>
						<summary>Why is a purchased course still locked?</summary>
						<p>
							The course belongs to a later grade, or its prerequisite has not been passed yet. It
							opens automatically once the student is eligible, and the 10-month period starts on
							that date.
						</p>
					</details>
					<details>
						<summary>What happens with my home school courses?</summary>
						<p>
							They are assessed through credit equivalency and recognized toward the OSSD. Please
							submit your home school transcripts at the end of each school year.
						</p>
					</details>
					<details>
						<summary>Can I take more than the minimum number of credits?</summary>
						<p>
							Yes. Additional courses can be added to the learning path. Please contact your mentor.
						</p>
					</details>
					<details>
						<summary>Why does an assessment show “Graded” without a mark?</summary>
						<p>
							It is an assessment as learning. It is checked as completed but does not receive a
							mark and does not count toward the overall grade. It still needs to be submitted.
						</p>
					</details>
					<details>
						<summary>Who should I contact if I am falling behind?</summary>
						<p>
							Your mentor. Early contact leaves time to agree on a study plan before the deadline.
						</p>
					</details>
				</div>
			</section>
		</StudyRulesContent>
	);
}
